import { observer } from 'mobx-react-lite'
import { useHotkey } from '@/hooks/keyboard-hooks'
import type { LegendEditorViewModel } from '@/features/legends/LegendEditor/LegendEditorViewModel'
import { PropertyInspectorContainer } from '@/features/legends/questions/components/PropertyInspector/PropertyInspectorContainer'
import { QuestionListSkeleton } from '@/features/legends/questions/components/QuestionList'
import { QuestionListContainer } from '@/features/legends/questions/components/QuestionList/QuestionListContainer'
import { QuestionsTab } from '@/features/legends/questions/components/QuestionsTab'
import { ToolboxContainer } from '@/features/legends/questions/components/Toolbox/ToolboxContainer'

const testId = 'legend-questions'

export interface QuestionsTabContainerProps {
  vm: LegendEditorViewModel
}

/**
 * The Questions tab: the toolbox, the questions as cards, and the inspector
 * for the selected one. Delete removes the selected question.
 */
export const QuestionsTabContainer = observer(function QuestionsTabContainer({
  vm,
}: QuestionsTabContainerProps) {
  const questionsVm = vm.questionsVm
  // Only while nothing has the focus: a focused card handles Delete
  // itself, and fields keep it for editing.
  useHotkey(['Delete'], () => {
    if (document.activeElement === document.body) {
      questionsVm.removeSelected()
    }
  })
  return (
    <QuestionsTab
      toolbox={<ToolboxContainer vm={questionsVm} />}
      inspector={
        questionsVm.isInspectorShown && (
          <PropertyInspectorContainer vm={questionsVm.inspectorVm} />
        )
      }
      testId={testId}
    >
      <QuestionListContainer vm={questionsVm} testId={testId} />
    </QuestionsTab>
  )
})

/**
 * The Questions tab while the legend loads.
 */
export const QuestionsTabSkeletonContainer = observer(
  function QuestionsTabSkeletonContainer({ vm }: QuestionsTabContainerProps) {
    return (
      <QuestionsTab
        toolbox={<ToolboxContainer vm={vm.questionsVm} isSkeleton />}
        testId={testId}
      >
        <QuestionListSkeleton />
      </QuestionsTab>
    )
  },
)
